import React, { useState, useEffect } from 'react';
import './ConfigPanel.css';
import { FaCog, FaTerminal, FaTrashAlt, FaSyncAlt, FaPlay, FaFont, FaEye, FaEyeSlash, FaQuestion } from 'react-icons/fa'; 
import { IconBaseProps } from 'react-icons';
import { submitCode, pollSubmissionResult, LANGUAGE_IDS } from '../../services/JudgeService';
import eventBus from '../../utils/eventBus';
import { callCloudflareAPI } from '../../utils/apiHelper';

const CogIcon = FaCog as React.FunctionComponent<IconBaseProps>;
const TerminalIcon = FaTerminal as React.FunctionComponent<IconBaseProps>;
const TrashIcon = FaTrashAlt as React.FunctionComponent<IconBaseProps>;
const SyncIcon = FaSyncAlt as React.FunctionComponent<IconBaseProps>;
const PlayIcon = FaPlay as React.FunctionComponent<IconBaseProps>;
const FontIcon = FaFont as React.FunctionComponent<IconBaseProps>; 
const EyeIcon = FaEye as React.FunctionComponent<IconBaseProps>; 
const EyeSlashIcon = FaEyeSlash as React.FunctionComponent<IconBaseProps>; 
const QuestionIcon = FaQuestion as React.FunctionComponent<IconBaseProps>; 

// 定义组件的 props 接口
interface ConfigPanelProps {
  code: string;
  language: string;
  stdin: string;
  setStdin: React.Dispatch<React.SetStateAction<string>>;
  output: string;
  setOutput: React.Dispatch<React.SetStateAction<string>>;
  compilerOptions: string;
  setCompilerOptions: React.Dispatch<React.SetStateAction<string>>;
  isCompiling: boolean;
  setIsCompiling: React.Dispatch<React.SetStateAction<boolean>>;
  isRunning: boolean;
  setIsRunning: React.Dispatch<React.SetStateAction<boolean>>;
}

// 定义从 JudgeService 返回的执行结果的结构
interface JudgeServiceStatus {
  id: number;
  description: string;
}

interface JudgeServiceResult {
  compile_output?: string | null; 
  stdout?: string | null;         
  stderr?: string | null;         
  status: JudgeServiceStatus;
  exit_code?: number | null;      
  time?: string | null;           
  memory?: number | null;         
  token?: string;                 
  // Add other fields if necessary based on Judge0's full response
}

interface ExecutionMetrics {
  exitCode: number | null;
  time: string | null;
  memory: number | null;
}

const ConfigPanel: React.FC<ConfigPanelProps> = ({
  code,
  language,
  stdin,
  setStdin,
  output,
  setOutput,
  compilerOptions,
  setCompilerOptions,
  isCompiling,
  setIsCompiling,
  isRunning,
  setIsRunning
}) => {
  // 使用从 props 传入的状态而不是内部状态
  const [gppVersion, setGppVersion] = useState<string>('g++ default');
  const [panelFontSize, setPanelFontSize] = useState<string>('14px'); 
  const [showCompilerOptions, setShowCompilerOptions] = useState<boolean>(language === 'cpp'); 
  const [showExecutionArgs, setShowExecutionArgs] = useState<boolean>(true);
  const [submissionToken, setSubmissionToken] = useState<string>('');
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [executionMetrics, setExecutionMetrics] = useState<ExecutionMetrics | null>(null);
  const [hasCompileError, setHasCompileError] = useState<boolean>(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setStdin(e.target.value);
  };

  const handleGppVersionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setGppVersion(e.target.value);
  };

  const handleCompileArgsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCompilerOptions(e.target.value);
  };

  const handlePanelFontSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPanelFontSize(e.target.value);
  };

  const toggleCompilerOptions = () => setShowCompilerOptions(prev => !prev);
  const toggleExecutionArgs = () => setShowExecutionArgs(prev => !prev);

  // 监听语言变化事件
  useEffect(() => {
    const handleLanguageChanged = (data: { language: string }) => {
      // 如果是 Python，隐藏编译器选项；如果是 C++，显示编译器选项
      setShowCompilerOptions(data.language === 'cpp');
    };

    // 注册事件监听器
    eventBus.on('languageChanged', handleLanguageChanged);

    // 组件卸载时移除事件监听器
    return () => {
      eventBus.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  // 分析编译错误的逻辑
  const handleAnalyzeError = async () => {
    console.log('分析编译错误按钮被点击');
    // 获取当前的编译错误信息
    const compilerError = output;
    console.log('编译错误信息:', compilerError);
    
    // 如果没有编译错误信息，则不执行任何操作
    if (!compilerError || compilerError.trim() === '') {
      console.log('没有编译错误信息，不执行操作');
      return;
    }
    
    // 尝试从错误信息中提取行号
    let errorLine = 1;
    const lineMatch = compilerError.match(/line\s+(\d+)/i) || compilerError.match(/\((\d+)\)/) || compilerError.match(/:(\d+):/);
    if (lineMatch && lineMatch[1]) {
      errorLine = parseInt(lineMatch[1], 10);
    }
    
    // 显示AI助手窗口
    console.log('发送 showAiAssistant 事件');
    eventBus.emit('showAiAssistant', {});
    
    // 构建用户消息 - 使用简洁的错误信息
    // 截取错误信息的前200个字符，避免消息过长
    const shortError = compilerError.length > 200 ? compilerError.substring(0, 200) + '...' : compilerError;
    const userMessage = `我的代码在第 ${errorLine} 行出现错误：${shortError}。请帮我解决。`;
    
    // 等待一下，确保AI助手窗口已经显示
    setTimeout(async () => {
      // 先发送用户消息到聊天窗口
      console.log('发送用户提问到聊天窗口');
      eventBus.emit('aiAnswer', {
        role: 'user',
        content: userMessage
      });
      
      // 发送加载状态事件
      console.log('发送加载状态事件');
      eventBus.emit('setLoading', { isLoading: true });
      
      try {
        // 直接调用 Cloudflare API
        console.log('直接调用 Cloudflare API');
        const data = await callCloudflareAPI('compileError', {
          fullCode: code || '',
          compilerError: compilerError,
          question: userMessage
        });
        
        // 将结果发送到聊天窗口
        console.log('将 API 响应发送到聊天窗口');
        eventBus.emit('aiAnswer', {
          role: 'assistant',
          content: data.markdown
        });
      } catch (error) {
        console.error('调用 Cloudflare API 失败:', error);
        // 发送错误消息到聊天窗口
        eventBus.emit('aiAnswer', {
          role: 'assistant',
          content: `抱歉，分析错误时出现问题：${error instanceof Error ? error.message : String(error)}`
        });
      } finally {
        // 无论成功还是失败，都关闭加载状态
        eventBus.emit('setLoading', { isLoading: false });
      }
    }, 500);
    
    // 重置按钮状态（可选，取决于是否需要连续点击）
    // setHasCompileError(false);
  };

  const handleRunCode = async () => {
    if (isCompiling || isRunning) return;
    
    setIsRunning(true);
    setOutput(''); // 清空旧输出
    setExecutionMetrics(null); // 清空旧指标
    setStatusMessage('运行中');
    // 重置编译错误状态
    setHasCompileError(false);
    
    try {
      // 检查语言是否有效并获取对应的语言ID
      if (!language || !(language in LANGUAGE_IDS)) {
        throw new Error(`不支持的语言: ${language}`);
      }
      
      // 获取当前语言的ID
      const languageId = LANGUAGE_IDS[language as keyof typeof LANGUAGE_IDS];
      
      // 提交代码到 Judge0 API
      const response = await submitCode(languageId, code, stdin, compilerOptions);
      setSubmissionToken(response.token);
      
      // 轮询获取执行结果
      const result: JudgeServiceResult = await pollSubmissionResult(response.token);
      
      // 处理执行结果
      let outputText = '';
      let hasError = false;
      
      if (result.compile_output) {
        outputText += `编译输出:
${result.compile_output}

`;
      }
      
      if (result.stdout) {
        outputText += `标准输出:
${result.stdout}
`;
      }
      
      if (result.status.id > 3) { // 如果有错误
        hasError = true;
        outputText += `错误: ${result.status.description}
`;
        if (result.stderr) {
          outputText += `${result.stderr}
`;
        }
      } else {
        setExecutionMetrics({
          exitCode: result.exit_code || 0,
          time: result.time || null, // 如果 result.time 是 undefined，则设为 null
          memory: result.memory || null, // 如果 result.memory 是 undefined，则设为 null
        });
      }
      
      setOutput(outputText);
      setStatusMessage(result.status.description);
      
      // 如果有编译错误或运行错误，设置编译错误状态为 true
      if (hasError) {
        setHasCompileError(true);
      }
    } catch (error) {
      console.error('运行错误:', error);
      setOutput(`运行错误: ${error instanceof Error ? error.message : String(error)}`);
      setStatusMessage('错误');
      // 运行时错误也视为可分析的错误
      setHasCompileError(true);
    } finally {
      setIsRunning(false);
    }
  };

  const panelTextStyle = { fontSize: panelFontSize };

  return (
    <div className="config-panel">
      <div className="config-toolbar">
        {/* 将所有控件放在一个组内，以便左对齐 */}
        <div className="toolbar-controls-group">
          <FontIcon className="toolbar-icon-label" />
          <select className="panel-font-select" value={panelFontSize} onChange={handlePanelFontSizeChange}>
            <option value="12px">12px</option>
            <option value="13px">13px</option>
            <option value="14px">14px</option>
            <option value="15px">15px</option>
            <option value="16px">16px</option>
            <option value="18px">18px</option>
            <option value="20px">20px</option>
            <option value="22px">22px</option>
            <option value="24px">24px</option>
            <option value="28px">28px</option>
            <option value="32px">32px</option>
          </select>
          <button 
            className={`toolbar-icon-button ${language !== 'cpp' ? 'disabled-button' : ''}`} 
            onClick={language === 'cpp' ? toggleCompilerOptions : undefined} 
            title={language === 'cpp' ? (showCompilerOptions ? "隐藏编译器选项" : "显示编译器选项") : "Python模式下不可用"}
            disabled={language !== 'cpp'}
          >
            {showCompilerOptions && language === 'cpp' ? <EyeSlashIcon /> : <EyeIcon />} <CogIcon />
          </button>
          <button className="toolbar-icon-button" onClick={toggleExecutionArgs} title={showExecutionArgs ? "隐藏执行参数" : "显示执行参数"}>
            {showExecutionArgs ? <EyeSlashIcon /> : <EyeIcon />} <TerminalIcon />
          </button>
          {/* 分析错误按钮 */}
          <button 
            className="toolbar-icon-button" 
            onClick={handleAnalyzeError} 
            title="分析编译错误"
            disabled={isCompiling || isRunning || !hasCompileError}
          >
            <QuestionIcon /> 
          </button>
          <button 
            className="toolbar-icon-button" 
            onClick={handleRunCode} 
            title="运行"
            disabled={isCompiling || isRunning}
          >
            <PlayIcon />
          </button>
        </div>
      </div>
      {showCompilerOptions && (
        <div className="compiler-options">
          <div className="compiler-options-row">
            <div className="gpp-version-container">
              <select 
                className="gpp-version-select" 
                value={gppVersion} 
                onChange={handleGppVersionChange}
                style={panelTextStyle}
              >
                <option value="g++ default">g++ default</option>
                <option value="g++ 11.2.0">g++ 11.2.0</option>
                <option value="g++ 9.3.0">g++ 9.3.0</option>
              </select>
              <div className="green-dot"></div>
            </div>
            <input 
              type="text" 
              className="compile-args-input" 
              value={compilerOptions} 
              onChange={handleCompileArgsChange} 
              placeholder="编译器参数..."
              style={panelTextStyle}
              disabled={isCompiling || isRunning}
            />
          </div>
        </div>
      )}
      {showExecutionArgs && (
        <div className="execution-panel">
          <div className="execution-header" style={panelTextStyle}>执行参数...</div>
          <textarea 
            className="input-area" 
            value={stdin} 
            onChange={handleInputChange} 
            placeholder="输入数据..."
            style={panelTextStyle}
            disabled={isCompiling || isRunning}
          ></textarea>
        </div>
      )}
      <div className="output-panel" style={panelTextStyle}>
        {statusMessage && <div className="status-message">{statusMessage}</div>}
        {executionMetrics && (
          <div className="execution-metrics-bar">
            <span className="metric-item">程序返回值: {executionMetrics.exitCode}</span>
            <span className="metric-item">执行时间: {executionMetrics.time}s</span>
            <span className="metric-item">内存使用: {executionMetrics.memory} KB</span>
          </div>
        )}
        <pre className="output-area">{output}</pre>
      </div>
      {/* 运行按钮现在在顶部工具栏，如果还需要底部的，可以取消注释或移除此逻辑 */}
      {/* <button className="run-button" onClick={handleRunCode}>运行</button> */}
    </div>
  );
};

export default ConfigPanel;
